// Page Names Enum from EVENT_LIST.md - Hardcoded values
export const PAGE_NAMES = {
    // Login & Authentication
    LOGIN: "login",
    FORGOT_PASSCODE: "forgot_passcode",

    // Signup & Onboarding
    SIGNUP: "signup",
    VERIFICATION: "verification",
    PROFILE: "profile",

    // Main Application
    DASHBOARD: "dashboard",

    // Loan Journey
    LOAN_HOME: "loan_home",
    LOAN_ELIGIBILITY_EMPLOY_VERIFICATION: "loan_eligiblity_employ_verification",
    BANK_VERIFICATION: "bank_verification",
    BANK_ACCOUNT_CONFIRMED: "bank_account_confirmed",
    CKYC_VERIFICATION: "ckyc_verification",
    FINAL_VERIFICATION: "final_verification", // Same as CKYC per EVENT_LIST.md
    USER_APPROVAL: "user_approval",
    VIDEO_VERIFICATION: "video_verification",
    E_SIGN_VERIFICATION: "e_sign_verification",
    E_MANDATE_REGISTRATION: "e-mandate registration",
    LOAN_DISBURSEMENT: "loan_disbursement",
    LOAN_HISTORY: "loan_history",

    // Other Features
    REPAYMENT: "repayment",
    SUPPORT: "support",
    NEED_HELP: "need_help",
    CONTACT_US: "contact_us",
    SETTINGS: "settings",
    FAQ_CONTACT: "faq_contact",
} as const;

export const GTM_EVENTS = {
    // ───────────────────── Page View ─────────────────────
    PAGE_VIEW: {
        page_view: "page_view",
    },
    E_COMMERCE: {
        view_item_list: "view_item_list",
        select_item: "select_item",
        view_item: "view_item",
        add_to_cart: "add_to_cart",
        begin_checkout: "begin_checkout",
        add_shipping_info: "add_shipping_info",
        add_payment_info: "add_payment_info",
        purchase: "purchase",
    },
    PAGE_VIEW_POPUP: {
        pop_up_opened: "pop_up_opened",
        pop_up_closed: "pop_up_closed",
    },
    LOGIN: {
        otp_sent: "otp_sent",
        resend_otp: "resend_otp",
        otp_verified: "mobile_otp_verified_success",
        mobile_otp_verified_fail: "mobile_otp_verified_fail",
        forgot_passcode_clicked: "forgot_passcode_clicked",
        forgot_otp_sent: "forgot_otp_sent",
        forgot_otp_verified: "forgot_otp_verified",
        passcode_changed: "passcode_changed",
        user_loggedin: "user_loggedin",
        field_name_updated: (field_name: string) => `${field_name}_updated`,
    },
    SIGNUP: {
        onboard_terms_accepted: "onboard_terms_accepted",
        document_consent_given: "document_consent_given",
    },
    DOCUMENT_VERIFICATION: {
        document_verification: (status: string) => `document_verification_${status}`,
        face_verification: (status: string) => `face_verification_${status}`,
    },
    PROFILE: {
        profile_terms_accepted: "profile_terms_accepted",
        profile_email_added: "profile_email_added",
        profile_email_verified: "profile_email_verified",
        profile_marital_selected: "profile_marital_selected",
        profile_linkedin_added: "profile_linkedin_added",
        profile_address_added: "profile_address_added",
        profile_passcode_created: "profile_passcode_created",
        profile_passcode_confirmed: "profile_passcode_confirmed",
        user_signedup: "user_signedup",
    },
    DASHBOARD: {
        dashboard_section_clicked: (section: string) => `dashboard_${section}_clicked`,
    },
    EMPLOYMENT_VERIFICATION: {
        loan_journey_begins: "loan_journey_begins",
        apply_now_clicked: "apply_now_clicked",
        employment_verification_begins: "employment_verification_begins",
        company_name_added: "company_name_added",
        company_address_added: "company_address_added",
        company_website_added: "company_website_added",
        phone_added: "phone_added",
        employment_details_submitted: "employment_details_submitted",
        work_email_submitted: "work_email_submitted",
        work_email_skipped: "work_email_skipped",
        work_email_verified: "work_email_verified",
        employment_verification_submitted: "company_details_verified_success",
        employment_verification_failed: "company_details_verified_fail",
    },

    BANK_VERIFICATION: {
        bank_verification_begins: "bank_verification_begins",
        penny_drop_begins: "penny_drop_begins",
        bank_name_selected: "bank_name_selected",
        bank_phoneno_selected: "bank_phoneno_selected",
        bank_alt_phoneno_added: "bank_alt_phoneno_added",
        bank_verify_method_begins: (method: "AA" | "NB") => `bank_verify_${method}_begins`,
        bank_accno_confirmed: "bank_accno_confirmed",
        bank_verification_result: (status: "success" | "failed") => `bank_verification_${status}`,
    },
    CKYC: {
        ckyc_verification_begins: "ckyc_verification_begins",
        ckyc_consent: (consent: "given" | "notgiven") => `ckyc_consent_${consent}`,
        ckyc_otp_verified: "ckyc_otp_verified",
        ckyc_skipped: "ckyc_skipped",
        ckyc_verification: (status: "success" | "fail") => `ckyc_verification_${status}`,
        final_verification_begins: "final_verification_begins",
        loan_result: (status: "approved" | "rejected") => `loan_${status}`,
    },
    USER_APPROVAL: {
        user_approval_begins: "user_approval_begins",
        loan_coupon_applied: "loan_coupon_applied",
        loan_structure_accepted: "loan_structure_accepted",
        loan_message_clicked: "loan_message_clicked",
        loan_agreement_accepted: "loan_agreement_accepted",
        loan_info_clicked: "loan_info_clicked",
    },
    VIDEO_VERIFICATION: {
        video_verification_begins: "video_verification_begins",
        record_video_clicked: "record_video_clicked",
        video_verification: (status: "success" | "failed") => `video_verification_${status}`,
        rerecord_video: "rerecord_video",
    },
    E_SIGN_VERIFICATION: {
        document_signed: (status: "success" | "failed") => `document_signed_${status}`,
        document_signing_begins: "document_signing_begins",
    },
    E_MANDATE: {
        emandate_approval_begins: "emandate_approval_begins",
        set_autopay_clicked: "set_autopay_clicked",
        emandate: (status: "success" | "failed") => `emandate_${status}`,
        loan_disbursed: "loan_disbursed",
        feedback_submitted: "feedback_submitted",
    },
    REPAYMENT: {
        repayment_begins: "repayment_begins",
        repay_now_clicked: "repay_now_clicked",
        view_repayment_summary: "view_repayment_summary",
        initiate_repayment: "initiate_repayment",
        add_repayment_info: "add_repayment_info",
        repayment_successful: "repayment_successful",
        repayment_failed: "repayment_failed",
        issue_repayment_clicked: "issue_repayment_clicked",
    },
    USER_DECLINE: {
        user_declined_loan_offer: "user_declined_loan_offer",
        feedback_rate_submit: "feedback_rate_submit",
        feedback_loan_amount: "feedback_loan_amount",
        feedback_loan_tenure: "feedback_loan_tenure",
        error_popup: "error_popup",
    },
    LOAN_HISTORY: {
        loan_details_viewed: "loan_details_viewed",
        loan_document_viewed: "loan_document_viewed",
    },
    FAQ_CONTACT: {
        support_type_clicked: "support_type_clicked",
        faq_question_clicked: "faq_question_clicked",
        faq_response_opened: "faq_response_opened",
        contact_us_clicked: (type: "call" | "chat") => `contact_us_${type}_clicked`,
        contact_us_chat_option: "contact_us_chat_option",
        need_help_clicked: "need_help_clicked",
        need_help_submitted: "need_help_submitted",
        contact_us_social_clicked: (social: "facebook" | "instagram" | "twitter" | "linkedin") =>
            `contact_us_${social}_clicked`,
        settings_option_clicked: "settings_option_clicked",
        account_deleted: "account_deleted",
    },

    // ───────────────────── New Events from Events.xlsx ─────────────────────
    ONBOARDING: {
        employment_submit: "employment_submit",
        income_submit: "income_submit",
        dob_submit_success: "dob_submit_success",
        pan_verified_success: "pan_verified_success",
        pan_verified_fail: "pan_verified_fail",
        cibil_fetched: "cibil_fetched",
        no_cibil_fetched: "no_cibil_fetched",
        company_details_submit: "company_details_submit",
        company_added_manually: "company_added_manually",
        email_details_skip: "email_details_skip",
        company_email_otp_verified_success: "company_email_otp_verified_success",
        company_email_otp_verified_fail: "company_email_otp_verified_fail",
        company_details_verified_success: "company_details_verified_success",
        company_details_verified_fail: "company_details_verified_fail",
        loan_purpose_submit: "loan_purpose_submit",
        aadhar_verified_success: "aadhar_verified_success",
        aadhar_verified_fail: "aadhar_verified_fail",
        address_submit: "address_submit",
        selfie_verified_success: "selfie_verified_success",
        selfie_verified_fail: "selfie_verified_fail",
        personal_email_otp_verified_success: "personal_email_otp_verified_success",
        personal_email_otp_verified_fail: "personal_email_otp_verified_fail",
        ckyc_verification_success: "ckyc_verification_success",
        ckyc_verification_fail: "ckyc_verification_fail",
        bank_details_submit: "bank_details_submit",
        bank_verified_success: "bank_verified_success",
        bank_verified_fail: "bank_verified_fail",
        video_verification_success: "video_verification_success",
        video_verification_fail: "video_verification_fail",
        loan_ready_for_disbursement: "loan_ready_for_disbursement",
        loan_declined: "loan_declined",
        loan_disbursed: "loan_disbursed",
    },
    DASHBOARD_EVENTS: {
        loan_closed: "loan_closed",
        emi_paid_success: "emi_paid_success",
        emi_paid_fail: "emi_paid_fail",
        refer_friend: "refer_friend",
    },
    USER_ENTRY: {
        login: "login",
        login_fail: "login_fail",
    },
    CUSTOMER_SUPPORT: {
        contact_us_initiated: "contact_us_initiated",
        contact_us_chat_started: "contact_us_chat_started",
    },
};

export const PLATFORM_ENUM = {
    web_app: "web_app",
    app_iOS: "app_iOS",
    app_android: "app_android",
} as const;

export enum TimelineEventStatesEnum {
    EMPLOYMENT_VERIFICATION_SUBMITTED = "company_details_verified_success",
    EMPLOYMENT_VERIFICATION_FAILED = "company_details_verified_fail",
    EMPLOYMENT_DETAILS_SUBMITTED = "employment_details_submitted",
    BANK_VERIFICATION_FAILED = "bank_verification_failed",
    BANK_VERIFICATION_SUCCESS = "bank_verification_success",
    CKYC_VERIFICATION_FAIL = "ckyc_verification_fail",
    CKYC_VERIFICATION_SUCCESS = "ckyc_verification_success",
    LOAN_RESULT_APPROVED = "loan_approved",
    LOAN_RESULT_REJECTED = "loan_rejected",
    E_MANDATE_SUCCESS = "emandate_success",
    E_MANDATE_FAILED = "emandate_failed",
    E_SIGN_DOCUMENT_SUCCESS = "document_signed_success",
    E_SIGN_DOCUMENT_FAILED = "document_signed_failed",
    VIDEO_VERIFICATION_SUCCESS = "video_verification_success",
    VIDEO_VERIFICATION_FAILED = "video_verification_failed",
    LOAN_DISBURSED = "loan_disbursed",
    EMPLOYMENT_VERIFICATION_BEGINS = "employment_verification_begins",
    ADD_TO_CART = "add_to_cart",
    FINAL_VERIFICATION_BEGINS = "final_verification_begins",
}

export enum RealTimeEventStatesEnum {
    PAYMENT_SUCCESS_FAILED = "PAYMENT_SUCCESS_FAILED",
    FINAL_VERIFICATION_VERIFIED = "FINAL_VERIFICATION_VERIFIED",
}

export const eventStates = {
    isFired: false,
    status: false,
};
